// Agent Routing System with OpenAI Structured Responses
import { createOpenAIProxy } from './openaiProxy';

// Structured routing schema
const ROUTING_SCHEMA = {
  name: "tool_routing",
  schema: {
    type: "object",
    properties: {
      tool: {
        type: "string",
        enum: ["document_rewriter_agent", "web_search", "knowledge_base", "both", "direct"]
      },
      confidence: { 
        type: "number",
        minimum: 0,
        maximum: 1
      },
      reasoning: { 
        type: "string" 
      }
    },
    required: ["tool", "confidence", "reasoning"]
  }
};

// Perform structured routing decision
export const performStructuredRouting = async (query, openaiParam, selectedModel, conversationHistory = [], attachedFiles = []) => {
  try {
    console.log('🤖 Performing structured routing analysis...');
    
    // Build context for routing decision
    const contextInfo = {
      hasFiles: attachedFiles.length > 0,
      fileTypes: attachedFiles.map(f => f.name.split('.').pop()).join(', '),
      conversationLength: conversationHistory.length,
      recentContext: conversationHistory.slice(-3).map(msg => `${msg.role}: ${msg.content.substring(0, 100)}`).join('\n')
    };
    
    console.log('🔍 Routing context:', contextInfo);
    console.log('🔍 Query:', query);
    console.log('🔍 Files:', attachedFiles.map(f => f.name));
    
    // Use secure OpenAI proxy instead of direct client
    const openai = createOpenAIProxy();
    
    const routingResponse = await openai.chat.completions.create({
      model: selectedModel,
      messages: [
        {
          role: 'system',
          content: `You are an intelligent routing system that decides which tools to use based on user queries.

**ROUTING RULES:**

**direct** - Choose when:
- User asks about content of uploaded files
- Simple questions that don't need external data
- General conversation or clarification requests
- Basic explanations or definitions

**document_rewriter_agent** - Choose when:
- User explicitly mentions "rewrite document", "create document", "generate document", "document template"
- User asks to "use as model", "similar to", "based on template"
- User mentions specific document types like "tax certificate", "legal document", "contract"
- User wants to modify or customize existing documents

**web_search** - Choose when:
- User mentions "current", "today", "latest", "news", "recent", "update", "now"
- User asks about current events, market conditions, or real-time information

**knowledge_base** - Choose when:
- User asks about legal matters, tax law, regulations, statutes, codes
- User mentions specific legal terms like IRC, Treasury Regulations, Revenue Rulings
- User asks for "compliance review", "legal review", "tax compliance"
- User needs authoritative legal or regulatory information

**both** - Choose when:
- User needs current information AND legal/regulatory context

**CONTEXT:**
- Files attached: ${contextInfo.hasFiles ? `Yes (${contextInfo.fileTypes})` : 'No'}
- Conversation history: ${contextInfo.conversationLength} messages
- Recent context: ${contextInfo.recentContext}

**IMPORTANT:** Be decisive and confident in your routing decision. Consider the user's primary intent.`
        },
        {
          role: 'user',
          content: `Query: "${query}"\n\nAnalyze this query and determine the best tool routing.`
        }
      ],
      response_format: {
        type: "json_schema",
        json_schema: ROUTING_SCHEMA
      },
      temperature: 1 // Low temperature for consistent routing decisions
    });
    
    const routing = JSON.parse(routingResponse.choices[0].message.content);
    
    console.log(`🎯 Routing decision: ${routing.tool} (confidence: ${routing.confidence})`);
    console.log(`📝 Reasoning: ${routing.reasoning}`);
    
    return {
      success: true,
      routing
    };
    
  } catch (error) {
    console.error('Error in structured routing:', error);
    
    return {
      success: false,
      error: error.message || 'Structured routing failed'
    };
  }
};

// Format routing decision for display
export const formatRoutingDecision = (routing) => {
  const toolNames = {
    'document_rewriter_agent': 'Document Rewriter Agent',
    'web_search': 'Web Search',
    'knowledge_base': 'Knowledge Base',
    'both': 'Web Search + Knowledge Base',
    'direct': 'Direct Response'
  };
  
  return {
    displayName: toolNames[routing.tool] || routing.tool,
    confidence: Math.round(routing.confidence * 100),
    reasoning: routing.reasoning
  };
};